import { PhysicalControlGroupEntry } from "../../modules/inventory/models/physical-control-group-entry";
import { ToastMessage } from "../../models/utilities/toast-message";
import * as utilityActions from "../../actions/utility-actions";
import { RawPhysicalInventoryConflicts } from "../../modules/inventory/models/physical-inventory-conflicts";
import { PhysicalInventoryDetails } from "../../modules/inventory/models/physical-inventory-details";
import { InventoryEntryUpdate } from "../../modules/inventory/models/physical-inventory-entry-update";
import { PhysicalInventoryListItem } from "../../modules/inventory/models/physical-inventory-list-item";
import { ResolveConflictsPayload } from "../../modules/inventory/models/resolve-conflicts-payload";
import { PhysicalInventoryShipments } from "../../modules/inventory/models/physical-inventory-shipments";
import { ShipmentsUpdate } from "../../modules/inventory/models/shipments-update";
import { BaseService } from "../../services/_base-service";
import { Api, ApiResponse, StatusCode } from "../../utils/api";
import { ApiEndpoints } from "../../utils/app-constants";
import { toShortDate } from "../../utils/date-utils";
import { PhysicalInventoryMiscOrders } from "../../modules/inventory/models/physical-inventory-misc-orders";
import { InventoryPhysicalAdjustmentInfo } from "../../modules/inventory/models/inventory-physical-adjustments";
import { InventoryPhysicalReport } from "../../modules/inventory/models/inventory-physical-report";

export class InventorySvc extends BaseService {

    public getPhysicalReports = async () => {

        this._showLoading();
        const response = new ApiResponse<PhysicalInventoryListItem[]>();

        try {
            const items = await Api.GetList(
                PhysicalInventoryListItem,
                ApiEndpoints.INVENTORY_PHYSICAL_REPORTS
            );

            if (items) {
                response.data = items;
            }
            else {
                response.data = [];
            }
            
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getPhysicalReportDetails = async (id: string): Promise<ApiResponse<PhysicalInventoryDetails>> => {
        return await this._get(
            PhysicalInventoryDetails,
            id,
            ApiEndpoints.INVENTORY_PHYSICAL_REPORT_DETAILS
        );
    }

    public getConflicts = async (reportId: string, controlNumber: string): Promise<ApiResponse<RawPhysicalInventoryConflicts>> => {

        this._showLoading();
        const response = new ApiResponse<RawPhysicalInventoryConflicts>();

        try {
            const entry = await Api.Get<RawPhysicalInventoryConflicts>(
                RawPhysicalInventoryConflicts,
                ApiEndpoints.INVENTORY_CONFLICTS.formattify(reportId, controlNumber)
            );

            if (entry) {
                response.data = entry;
            };

            response.status = StatusCode.Success;

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getControlGroupEntry = async (reportId: string, controlNumber: string, entryNumber: number): Promise<ApiResponse<PhysicalControlGroupEntry>> => {

        this._showLoading();
        const response = new ApiResponse<PhysicalControlGroupEntry>();

        try {
            const entry = await Api.Get<PhysicalControlGroupEntry>(
                PhysicalControlGroupEntry,
                ApiEndpoints.INVENTORY_PHYSICAL_CONTROL_GROUP_ENTRY.formattify(reportId, controlNumber, entryNumber)
            );

            if (entry) {
                response.data = entry;
            };

            response.status = StatusCode.Success;

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public updateControlGroupEntry = async (reportId: string, payload: InventoryEntryUpdate): Promise<ApiResponse<any>> => {
        this._showLoading();
        const response = new ApiResponse<any>();

        try {
            const result = await Api.Put(
                ApiEndpoints.INVENTORY_PHYSICAL_CONTROL_GROUP_ENTRY.formattify(reportId, payload.controlNumber, payload.entryNumber),
                payload
            );

            response.data = result;
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Control Group entry updated.", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public createPhysicalReport = async (name: string, date: Date, controlMapFile: any) => {
        var data = new FormData();
        data.append("file", controlMapFile);

        this._showLoading();
        const response = new ApiResponse<PhysicalInventoryListItem>();
        try {
            var dateStr = toShortDate(date);
            var url = ApiEndpoints.INVENTORY_PHYSICAL_CREATE_REPORT.formattify(dateStr, name);
            const result = await Api.Post(
                url,
                data,
                'multipart/form-data'
            );

            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Report submitted.", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }
        this._hideLoading();
        return response;
    }

    public resolveConflicts = async (payload: ResolveConflictsPayload): Promise<ApiResponse<any>> => {
        this._showLoading();
        const response = new ApiResponse<any>();

        try {
            const result = await Api.Put(
                ApiEndpoints.INVENTORY_PHYSICAL_RESOLVE_CONFLICTS.formattify(payload.reportId),
                payload
            );

            response.data = result;
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Conflict Resolved", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getShipmentData = async (reportId: string): Promise<ApiResponse<PhysicalInventoryShipments>> => {

        this._showLoading();
        const response = new ApiResponse<PhysicalInventoryShipments>();

        try {
            const entry = await Api.Get<PhysicalInventoryShipments>(
                PhysicalInventoryShipments,
                ApiEndpoints.INVENTORY_PHYSICAL_SHIPMENTS.formattify(reportId)
            );

            if (entry) {
                response.data = entry;
            };

            response.status = StatusCode.Success;

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public updateShipments = async (reportId: string, payload: ShipmentsUpdate): Promise<ApiResponse<any>> => {
        this._showLoading();
        const response = new ApiResponse<any>();

        try {
            const result = await Api.Post(
                ApiEndpoints.INVENTORY_PHYSICAL_SHIPMENTS.formattify(reportId),
                payload
            );

            response.data = result;
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Shipments Updated", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getMiscOrderData = async (reportId: string): Promise<ApiResponse<PhysicalInventoryMiscOrders>> => {

        this._showLoading();
        const response = new ApiResponse<PhysicalInventoryMiscOrders>();

        try {
            const entry = await Api.Get<PhysicalInventoryMiscOrders>(
                PhysicalInventoryMiscOrders,
                ApiEndpoints.INVENTORY_PHYSICAL_ORDERS.formattify(reportId)
            );

            if (entry) {
                response.data = entry;
            };

            response.status = StatusCode.Success;

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public updateMiscOrders = async (reportId: string, controlMapFile: any) => {

        var data = new FormData();
        data.append("file", controlMapFile);

        this._showLoading();
        const response = new ApiResponse<PhysicalInventoryListItem>();
        try {
            var url = ApiEndpoints.INVENTORY_PHYSICAL_ORDERS.formattify(reportId);
            const result = await Api.Post(
                url,
                data,
                'multipart/form-data'
            );

            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Orders Updated", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }
        this._hideLoading();
        return response;
    }

    public getAdjustmentData = async (reportId: string): Promise<ApiResponse<InventoryPhysicalAdjustmentInfo>> => {

        this._showLoading();
        const response = new ApiResponse<InventoryPhysicalAdjustmentInfo>();

        try {
            const entry = await Api.Get<InventoryPhysicalAdjustmentInfo>(
                InventoryPhysicalAdjustmentInfo,
                ApiEndpoints.INVENTORY_PHYSICAL_ADJUSTMENTS.formattify(reportId)
            );

            if (entry) {
                response.data = entry;
            };

            response.status = StatusCode.Success;

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public updateAdjustments = async (reportId: string, payload: InventoryPhysicalAdjustmentInfo): Promise<ApiResponse<any>> => {
        this._showLoading();
        const response = new ApiResponse<any>();

        try {
            const result = await Api.Post(
                ApiEndpoints.INVENTORY_PHYSICAL_ADJUSTMENTS.formattify(reportId),
                payload
            );

            response.data = result;
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Adjustments saved.", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public refreshPricing = async (reportId: string) => {

        this._showLoading();
        const response = new ApiResponse<any>();

        try {
            const result = await Api.Post(
                ApiEndpoints.INVENTORY_PHYSICAL_REFRESH_PRICING.formattify(reportId),
                { }
            );

            response.data = result;
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Pricing Refresh Initiated", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public getFinalReport = async (reportId: string) => {
        this._showLoading();
        const response = new ApiResponse<InventoryPhysicalReport>();

        try {
            const entry = await Api.Get<InventoryPhysicalReport>(
                InventoryPhysicalReport,
                ApiEndpoints.INVENTORY_FINAL_REPORT.formattify(reportId)
            );

            if (entry) {
                response.data = entry;
            };

            response.status = StatusCode.Success;

        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }


    public finalizeReport = async (reportId: string) => {

        this._showLoading();
        const response = new ApiResponse<PhysicalInventoryListItem>();

        try {
            const result = await Api.Post(
                ApiEndpoints.INVENTORY_PHYSICAL_FINALIZE.formattify(reportId),
                {}
            );

            response.data = result;
            this.store.dispatch(utilityActions.showToastNotification(new ToastMessage("Report Finalized", "success")));
            response.status = StatusCode.Success;
        }
        catch (err) {
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }
}
